import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest, apiRequest, graphConfig, armRequest, graphSecurityRequest } from '../authConfig';

class AuthService {
  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.account = null;
    this.userProfile = null;
    this.registerUserPromise = null;
  }

  async initialize() {
    try {
      await this.msalInstance.initialize();
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.account = accounts[0];
        this.msalInstance.setActiveAccount(this.account);
        await this.loadUserProfile();
        
        // Register user in database if they're already authenticated
        await this.registerUserInDatabase();
      }
    } catch (error) {
      console.error('MSAL initialization failed:', error);
    }
  }

  async loadUserProfile() {
    try {
      if (!this.account) return;

      // Acquire token specifically for Microsoft Graph
      const graphTokenResponse = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: this.account
      });
      const token = graphTokenResponse.accessToken;
      const response = await fetch(graphConfig.graphMeEndpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        this.userProfile = await response.json();
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  }

  async login() {
    try {
      const response = await this.msalInstance.loginPopup(loginRequest);
      this.account = response.account;
      this.msalInstance.setActiveAccount(this.account);
      await this.loadUserProfile();
      
      // Register user in the database after successful login
      await this.registerUserInDatabase();
      
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async logout() {
    try {
      // Clear local state first
      this.account = null;
      this.userProfile = null;
      
      // Clear all accounts from MSAL
      this.msalInstance.clearCache();
      
      // Perform logout
      await this.msalInstance.logoutPopup({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state
      this.account = null;
      this.userProfile = null;
      this.msalInstance.clearCache();
      throw error;
    }
  }

  clearAuthState() {
    this.account = null;
    this.userProfile = null;
    this.msalInstance.clearCache();
  }

  async getAccessToken() {
    try {
      if (!this.account) {
        throw new Error('No account found');
      }

      // Acquire token specifically for the custom API
      const response = await this.msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: this.account
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await this.msalInstance.acquireTokenPopup(apiRequest);
          return response.accessToken;
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError);
          throw popupError;
        }
      }
      throw error;
    }
  }

  async getArmToken(interactive = false) {
    if (!this.account) throw new Error('Not authenticated');
    const request = { ...armRequest, account: this.account };
    try {
      const result = await this.msalInstance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (err) {
      if (interactive || err instanceof InteractionRequiredAuthError) {
        const result = await this.msalInstance.acquireTokenPopup(request);
        return result.accessToken;
      }
      throw err;
    }
  }

  // Token for Microsoft 365 Secure Score (SecurityEvents.Read.All + Organization.Read.All).
  // These scopes require admin consent on the app registration before they will work.
  async getGraphSecurityToken(interactive = false) {
    if (!this.account) throw new Error('Not authenticated');
    const request = { ...graphSecurityRequest, account: this.account };
    try {
      const result = await this.msalInstance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (err) {
      if (interactive || err instanceof InteractionRequiredAuthError) {
        const result = await this.msalInstance.acquireTokenPopup(request);
        return result.accessToken;
      }
      throw err;
    }
  }

  async makeAuthenticatedRequest(url, options = {}) {
    try {
      const token = await this.getAccessToken();
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      };

      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        let detail = '';
        try {
          const body = await response.json();
          detail = body?.message ? `: ${body.message}` : '';
        } catch {
          /* ignore non-JSON error bodies */
        }
        const error = new Error(`HTTP error! status: ${response.status}${detail}`);
        error.status = response.status;
        throw error;
      }

      // Handle 204 No Content responses (like DELETE operations)
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      // Expected during first provision / role probes — callers handle these.
      if (error?.status !== 404 && error?.status !== 403) {
        console.error('Authenticated request failed:', error);
      }
      throw error;
    }
  }

  isAuthenticated() {
    return this.account !== null;
  }

  getUser() {
    return this.account;
  }

  getUserProfile() {
    return this.userProfile;
  }

  getUserId() {
    const oid = this.account?.idTokenClaims?.oid;
    return oid || this.userProfile?.id || this.account?.localAccountId;
  }

  getUserName() {
    return this.userProfile?.displayName || this.account?.name;
  }

  getUserEmail() {
    return this.userProfile?.mail || this.userProfile?.userPrincipalName || this.account?.username;
  }

  async ensureRegisteredInDatabase() {
    if (!this.isAuthenticated()) {
      return null;
    }

    const userId = this.getUserId();
    if (!userId) {
      return null;
    }

    if (this.registerUserPromise) {
      return this.registerUserPromise;
    }

    this.registerUserPromise = (async () => {
      const { apiService } = await import('./apiService');
      return apiService.registerUser();
    })();

    try {
      return await this.registerUserPromise;
    } catch (error) {
      this.registerUserPromise = null;
      throw error;
    }
  }

  async registerUserInDatabase() {
    try {
      await this.ensureRegisteredInDatabase();
    } catch (error) {
      const status = error?.message?.match(/status: (\d+)/)?.[1];
      if (status !== '429') {
        console.warn('Failed to register user in database:', error);
      }
    }
  }
}

export const authService = new AuthService(); 